import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import {
	Calculator,
	Calendar,
	Camera01,
	CheckSquareBroken,
	Clock,
	Toggle02Left,
	Type01,
} from "@untitledui/icons";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { type Column, TableCustom } from "@/components/ui/table";
import {
	formulariosPerguntasBuscar,
	formulariosPerguntasNova,
} from "@/services/analytics/events";
import { useDebouncedCommit } from "@/shared/hooks/use-debounced-commit";
import {
	QuestionType as QuestionTypeEnum,
	QuestionTypeLabels,
} from "@/types/question";
import { useGetQuestions } from "../../hooks";
import type {
	ChecklistQuestion,
	GetAllQuestionsParams,
	QuestionType,
} from "../../types";

const SEARCH_DEBOUNCE_MS = 500;

// Função para converter enum para string type
const enumToTypeString = (type: QuestionTypeEnum): QuestionType => {
	const map: Record<QuestionTypeEnum, QuestionType> = {
		[QuestionTypeEnum.Option]: "Option",
		[QuestionTypeEnum.Date]: "Date",
		[QuestionTypeEnum.Time]: "Time",
		[QuestionTypeEnum.Text]: "Text",
		[QuestionTypeEnum.Number]: "Number",
		[QuestionTypeEnum.File]: "File",
		[QuestionTypeEnum.Boolean]: "Boolean",
	};
	return map[type];
};

const typeMap: Partial<
	Record<
		QuestionType,
		{ label: string; icon: React.ComponentType<{ className?: string }> }
	>
> = {
	Option: {
		label: QuestionTypeLabels[QuestionTypeEnum.Option],
		icon: CheckSquareBroken,
	},
	Date: {
		label: QuestionTypeLabels[QuestionTypeEnum.Date],
		icon: Calendar,
	},
	Time: {
		label: QuestionTypeLabels[QuestionTypeEnum.Time],
		icon: Clock,
	},
	Text: {
		label: QuestionTypeLabels[QuestionTypeEnum.Text],
		icon: Type01,
	},
	Number: {
		label: QuestionTypeLabels[QuestionTypeEnum.Number],
		icon: Calculator,
	},
	File: {
		label: QuestionTypeLabels[QuestionTypeEnum.File],
		icon: Camera01,
	},
	Boolean: {
		label: QuestionTypeLabels[QuestionTypeEnum.Boolean],
		icon: Toggle02Left,
	},
	Checklist: {
		label: "Checklist Padrão",
		icon: CheckSquareBroken,
	},
};

// Remover DateTime do filtro, apenas incluir os tipos que existem no cadastro
const questionTypes = [
	{ id: "all", label: "Tipo de pergunta" },

	{
		id: enumToTypeString(QuestionTypeEnum.Option),
		label: typeMap.Option!.label,
		icon: typeMap.Option!.icon,
	},
	{
		id: "Checklist",
		label: typeMap.Checklist!.label,
		icon: typeMap.Checklist!.icon,
	},
	{
		id: enumToTypeString(QuestionTypeEnum.Date),
		label: typeMap.Date!.label,
		icon: typeMap.Date!.icon,
	},
	{
		id: enumToTypeString(QuestionTypeEnum.Time),
		label: typeMap.Time!.label,
		icon: typeMap.Time!.icon,
	},
	{
		id: enumToTypeString(QuestionTypeEnum.Text),
		label: typeMap.Text!.label,
		icon: typeMap.Text!.icon,
	},
	{
		id: enumToTypeString(QuestionTypeEnum.Number),
		label: typeMap.Number!.label,
		icon: typeMap.Number!.icon,
	},
	{
		id: enumToTypeString(QuestionTypeEnum.File),
		label: typeMap.File!.label,
		icon: typeMap.File!.icon,
	},
	{
		id: enumToTypeString(QuestionTypeEnum.Boolean),
		label: typeMap.Boolean!.label,
		icon: typeMap.Boolean!.icon,
	},
];

export function QuestionsList() {
	const navigate = useNavigate();

	const [searchTerm, setSearchTerm] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
	const [currentPage, setCurrentPage] = useState(1);
	const itemsPerPage = 10;
	const [selectedType, setSelectedType] = useState<string>("all");
	const resetPage = () => setCurrentPage(1);

	const criteria = useMemo<Omit<GetAllQuestionsParams, "pageNumber">>(() => {
		const params: Omit<GetAllQuestionsParams, "pageNumber"> = {
			prompt: debouncedSearch || undefined,
			pageSize: itemsPerPage,
		};

		if (selectedType === "Checklist") {
			// Checklist Padrão: type=Option + optionDisplayType=1 (Icon)
			params.type = "Option";
			params.optionDisplayType = 1;
		} else if (selectedType === "Option") {
			// Seleção normal: type=Option + optionDisplayType=2 (Checkbox)
			params.type = "Option";
			params.optionDisplayType = 2;
		} else if (selectedType !== "all") {
			// Outros tipos: apenas type, sem optionDisplayType
			params.type = selectedType as QuestionType;
		}

		return params;
	}, [debouncedSearch, selectedType]);

	const queryParams = useMemo<GetAllQuestionsParams>(
		() => ({ ...criteria, pageNumber: currentPage }),
		[criteria, currentPage]
	);

	const questionsQuery = useGetQuestions(queryParams);
	const { data: questionsData, isFetching, isError, refetch } = questionsQuery;

	const { total: totalPages } = usePaginationTotal({
		page: currentPage,
		onPageChange: setCurrentPage,
		criteria,
		totalPages: questionsData?.totalPages,
		query: questionsQuery,
	});

	const columns = useMemo<Column<ChecklistQuestion>[]>(
		() => [
			{
				id: "title",
				label: "Título da pergunta",
				render: (q) => <div className="font-medium">{q.prompt}</div>,
			},
			{
				id: "type",
				label: "Tipo",
				render: (q) => {
					// Se for Option com optionDisplayType = 1, mostrar como Checklist Padrão
					const isChecklistPadrao =
						q.type === "Option" && q.optionDisplayType === 1;
					// Se for Checklist (tipo antigo), usar o mesmo ícone e label de Option
					const displayType =
						q.type === "Checklist" || isChecklistPadrao ? "Option" : q.type;
					const typeInfo = typeMap[displayType] ?? {
						label:
							q.type === "Checklist" || isChecklistPadrao
								? "Checklist padrão"
								: q.type,
						icon: Type01,
					};
					const Icon = typeInfo.icon;
					const label =
						q.type === "Checklist" || isChecklistPadrao
							? "Checklist padrão"
							: typeInfo.label;

					return (
						<div className="flex items-center gap-2 whitespace-nowrap">
							<span aria-hidden className="shrink-0">
								<Icon className="size-4 text-fg-quaternary" />
							</span>
							<span>{label}</span>
						</div>
					);
				},
			},
			{
				id: "formsInUse",
				label: "Formulário em uso",
				render: (q) => <span>{q.templateUsageCount}</span>,
			},
			{
				id: "lastUpdate",
				label: "Última atualização",
				render: (q) => (
					<span className="whitespace-nowrap">
						{new Date(q.createdAt).toLocaleString("pt-BR")}
					</span>
				),
			},
		],
		[]
	);

	useDebouncedCommit({
		value: searchTerm,
		applied: debouncedSearch,
		delay: SEARCH_DEBOUNCE_MS,
		onCommit: (term) => {
			setDebouncedSearch(term);
			setCurrentPage(1);
			if (term) {
				formulariosPerguntasBuscar(term);
			}
		},
	});

	return (
		<TableCustom
			addButtonLabel="Nova pergunta"
			columns={columns}
			currentPage={currentPage}
			data={questionsData?.items || []}
			errorMessage="Não foi possível carregar as perguntas."
			filterBarLabel="Filtros de perguntas"
			filterOptions={questionTypes}
			filterPlaceholder="Tipo de pergunta"
			filterValue={selectedType ?? "all"}
			isError={isError}
			isLoading={isFetching}
			layout="page"
			onAdd={() => {
				formulariosPerguntasNova();
				navigate("/questions/new");
			}}
			onFilterChange={(value: string) => {
				setSelectedType((prev) => {
					const nextValue = prev === value ? "all" : value;
					return nextValue;
				});

				setCurrentPage(1);
			}}
			onPageChange={setCurrentPage}
			onRetry={() => {
				refetch();
			}}
			onSearchChange={(value: string) => {
				setSearchTerm(value);
				resetPage();
			}}
			searchLabel="Pesquise pela pergunta"
			searchPlaceholder="Pesquise pela pergunta..."
			searchValue={searchTerm}
			title="Lista de perguntas"
			totalPages={totalPages}
		/>
	);
}
